/**
 * In-world speech bubbles: little white comic tags that follow creatures.
 * Screen placement comes from a projection callback so this module never
 * touches the renderer. Max 3 concurrent; keyed so a creature's newer
 * utterance replaces its older one.
 */

export interface BubbleProjector {
  (x: number, y: number, z: number): { px: number; py: number; visible: boolean };
}

interface Bubble {
  key: number;
  wx: number;
  wy: number;
  wz: number;
  ttl: number;
  el: HTMLDivElement;
}

const MAX_BUBBLES = 3;
const DEFAULT_TTL_S = 4;

const STYLE_ID = 'w94-bubble-styles';
const BUBBLE_CSS = `
.w94-bubble {
  position: fixed;
  left: 0;
  top: 0;
  transform: translate(-50%, -110%);
  background: #fff;
  color: #000;
  border: 1px solid #241C12;
  box-shadow: 2px 2px 0 #000;
  font: 11px/1.3 Consolas, 'Courier New', monospace;
  padding: 3px 7px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  animation: w94-bubble-pop 140ms ease-out;
}
.w94-bubble::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -4px;
  width: 6px;
  height: 6px;
  background: #fff;
  border-right: 1px solid #241C12;
  border-bottom: 1px solid #241C12;
  transform: translateX(-50%) rotate(45deg);
}
@keyframes w94-bubble-pop {
  from { transform: translate(-50%, -110%) scale(0.55); opacity: 0; }
  to { transform: translate(-50%, -110%) scale(1); opacity: 1; }
}
`;

function injectBubbleStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = BUBBLE_CSS;
  document.head.appendChild(style);
}

export class Bubbles {
  private readonly parent: HTMLElement;
  private readonly project: BubbleProjector;
  private readonly live: Bubble[] = [];

  constructor(parent: HTMLElement, project: BubbleProjector) {
    injectBubbleStyles();
    this.parent = parent;
    this.project = project;
  }

  /**
   * Show a bubble that follows world position (wx,wy,wz) for ttlSec.
   * A new bubble with the same key replaces the old; max 3 concurrent,
   * oldest evicted.
   */
  say(
    key: number,
    wx: number,
    wy: number,
    wz: number,
    content: string | DocumentFragment,
    ttlSec = DEFAULT_TTL_S,
  ): void {
    const existing = this.live.findIndex((b) => b.key === key);
    if (existing !== -1) this.evict(existing);
    while (this.live.length >= MAX_BUBBLES) this.evict(0);

    const el = document.createElement('div');
    el.className = 'w94-bubble';
    el.style.visibility = 'hidden'; // until first projection lands
    if (typeof content === 'string') {
      el.textContent = content;
    } else {
      el.appendChild(content);
    }
    this.parent.appendChild(el);

    const bubble: Bubble = { key, wx, wy, wz, ttl: ttlSec, el };
    this.live.push(bubble);
    this.place(bubble);
  }

  /** Call every frame: repositions live bubbles and expires stale ones. */
  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const bubble = this.live[i];
      if (!bubble) continue;
      bubble.ttl -= dt;
      if (bubble.ttl <= 0) {
        this.evict(i);
        continue;
      }
      this.place(bubble);
    }
  }

  private place(bubble: Bubble): void {
    const p = this.project(bubble.wx, bubble.wy, bubble.wz);
    if (!p.visible) {
      bubble.el.style.visibility = 'hidden';
      return;
    }
    bubble.el.style.visibility = '';
    bubble.el.style.left = `${Math.round(p.px)}px`;
    bubble.el.style.top = `${Math.round(p.py)}px`;
  }

  private evict(index: number): void {
    const bubble = this.live[index];
    if (!bubble) return;
    bubble.el.remove();
    this.live.splice(index, 1);
  }
}
